/* Public pages outside the shell (no sign-in, minimal layout): links the API hands out.
   /f/:slug                 a published form (sheets feature's PublicFormPage)
   /shared/:token           a shared item (collab share registry, read-only, redacted by the API)
   /shared/:type/:token     typed share links: types with an in-app viewer open it, others use the generic page
   /replay/:token           an agent task replay (agents feature's ReplayPlayer)
   /p/:slug                 a published site: what it is and a link to it on the sandbox origin
   /share-target            landing for the OS share sheet (the service worker sends signed-out shares here) */
import { lazy, Suspense, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ExternalLink, FileText, Flag, GitFork, Globe, Share2, ShieldCheck } from 'lucide-react';
import { Banner, Button, Card, Chip, EmptyState, MarkdownStream, Skeleton } from '../ui';
import { OptionalSlot, firstLoader, optionalComponent } from '../ui/optional';
import { api, ApiError } from '../lib/api';
import { API } from '../lib/endpoints';
import { AiDisclosureLink } from './widgets';
import { useSession } from './session';

const errText = (e: unknown) => (e instanceof ApiError ? (e.status === 404 ? 'This link does not exist or was revoked.' : e.isNetwork ? 'Crosspoint AI + cannot be reached right now. Try again in a moment.' : e.message) : 'Something went wrong.');

/* ---------- layout ---------- */
export function PublicLayout({ title, children }: { title: string; children: ReactNode }) {
  const s = useSession();
  const loc = useLocation();
  useEffect(() => { document.title = `${title} · Crosspoint AI +`; }, [title]);
  return (
    <div className="shell public-shell" data-public="true">
      <a href="#main" className="skip-link">Skip to content</a>
      <header className="titlebar">
        <Link to="/" className="tb-brand" aria-label="Crosspoint AI + home"><span className="logo" aria-hidden="true">+</span><span>Crosspoint AI +</span></Link>
        <span className="sp" />
        {s.status === 'authenticated'
          ? <Link className="btn sm" to="/">Open the app</Link>
          : <Link className="btn sm primary" to="/welcome" state={{ from: loc.pathname + loc.search }}>Sign in</Link>}
      </header>
      <main id="main" className="shell-main public-main" tabIndex={-1} aria-label={title}>
        <div className="route-root"><div className="screen-body"><div className="reading stack">{children}</div></div></div>
      </main>
      <footer className="public-foot small muted">
        <AiDisclosureLink /> <span aria-hidden="true">·</span> <span>Shared with Crosspoint AI +. Never enter passwords or card numbers on a shared page.</span>
      </footer>
    </div>
  );
}

/* ---------- /f/:slug ---------- */
const formsLoader = firstLoader(import.meta.glob('../features/sheets/Forms.tsx'));
const sheetsBackendLoader = firstLoader(import.meta.glob('../features/sheets/backend.ts'));
type FormPageProps = { slug: string };
const PublicFormPage = formsLoader && sheetsBackendLoader
  ? lazy(async () => {
    const [forms, backend] = (await Promise.all([formsLoader(), sheetsBackendLoader()])) as [
      { PublicFormPage: (p: { backend: unknown; slug: string }) => ReactNode },
      { httpSheets: unknown },
    ];
    const Page = forms.PublicFormPage;
    return { default: ({ slug }: FormPageProps) => <Page backend={backend.httpSheets} slug={slug} /> };
  })
  : null;

export function PublicFormRoute() {
  const { slug = '' } = useParams();
  return (
    <PublicLayout title="Form">
      <div data-cap="OFFICE-08">
        {PublicFormPage
          ? <Suspense fallback={<Skeleton height={240} />}><PublicFormPage slug={slug} /></Suspense>
          : <EmptyState icon={FileText} title="Forms are not available" purpose="This installation has no forms feature." />}
      </div>
    </PublicLayout>
  );
}

/* ---------- /shared/:type/:token and /shared/:token ---------- */
/** Share types whose own feature has a viewer inside the app (sign-in required there). */
export const IN_APP_SHARED: Record<string, string> = {
  sheets: '/sheets/shared/', sheet: '/sheets/shared/', workbook: '/sheets/shared/',
  build: '/build/shared/', artifact: '/build/shared/', artifacts: '/build/shared/',
  docs: '/docs/shared/', doc: '/docs/shared/',
};

interface SharedView {
  share: { id: string; resourceType: string; title?: string | null; visibility: string; capabilities: string[]; pinnedVersion?: string | null; expiresAt?: string | null; viewCount?: number; owner?: { name?: string | null } };
  content: unknown;
  canFork?: boolean;
  canComment?: boolean;
}

function contentText(c: unknown): string | null {
  if (typeof c === 'string') return c;
  if (c && typeof c === 'object') {
    const o = c as Record<string, unknown>;
    for (const k of ['markdown', 'text', 'body', 'content', 'report']) if (typeof o[k] === 'string') return o[k] as string;
    if (Array.isArray(o.messages)) {
      return (o.messages as { role?: string; content?: string; text?: string }[])
        .map((m) => `**${m.role === 'user' ? 'You' : m.role === 'assistant' ? 'Crosspoint AI +' : m.role ?? ''}:** ${m.content ?? m.text ?? ''}`).join('\n\n');
    }
  }
  return null;
}

export function SharedRoute() {
  const { type, token = '' } = useParams();
  if (type && IN_APP_SHARED[type]) return <Navigate to={`${IN_APP_SHARED[type]}${encodeURIComponent(token)}`} replace />;
  return <SharedItem token={token} />;
}

function SharedItem({ token }: { token: string }) {
  const s = useSession();
  const navigate = useNavigate();
  const loc = useLocation();
  const q = useQuery({ queryKey: ['shared', token], queryFn: () => api.get<SharedView>(API.shared(token)), retry: false, staleTime: 60_000 });
  const fork = useMutation({ mutationFn: () => api.post<{ fork?: { path?: string; url?: string } }>(API.sharedFork(token)) });
  const v = q.data;
  const text = v ? contentText(v.content) : null;
  return (
    <PublicLayout title={v?.share.title ?? 'Shared item'}>
      {q.isPending && <Skeleton height={200} />}
      {q.isError && <Banner tone="danger" title="This link does not open">{errText(q.error)}</Banner>}
      {v && (
        <article className="stack" data-cap="COLLAB-01" aria-label="Shared item">
          <div className="row">
            <Share2 size={18} aria-hidden="true" />
            <h1 style={{ font: 'var(--t-title-l)' }}>{v.share.title ?? 'Shared item'}</h1>
          </div>
          <div className="row small muted">
            <Chip>{v.share.resourceType}</Chip>
            {v.share.owner?.name && <span>Shared by {v.share.owner.name}</span>}
            {v.share.pinnedVersion && <span>Version {v.share.pinnedVersion}</span>}
            {v.share.expiresAt && <span>Link expires {new Date(v.share.expiresAt).toLocaleDateString()}</span>}
          </div>
          <Card>
            {text ? <MarkdownStream>{text}</MarkdownStream> : <pre className="small" style={{ whiteSpace: 'pre-wrap' }}>{JSON.stringify(v.content, null, 2)}</pre>}
          </Card>
          <p className="small muted"><ShieldCheck size={12} aria-hidden="true" /> Personal details were removed before sharing. Replies made with AI can be wrong.</p>
          {v.share.capabilities.includes('fork') && (
            s.status === 'authenticated'
              ? <div><Button variant="primary" icon={<GitFork size={14} aria-hidden="true" />} disabled={fork.isPending} onClick={() => fork.mutate(undefined, { onSuccess: (r) => { if (r.fork?.path) navigate(r.fork.path); } })}>Make a copy</Button>
                {fork.isError && <Banner tone="danger" title="Could not make a copy">{errText(fork.error)}</Banner>}
                {fork.isSuccess && !fork.data.fork?.path && <p className="small">Copied to your account.</p>}</div>
              : <p className="small"><Link to="/welcome" state={{ from: loc.pathname }}>Sign in</Link> to make your own copy.</p>
          )}
        </article>
      )}
    </PublicLayout>
  );
}

/* ---------- /replay/:token ---------- */
interface Replay { title: string; goal: string; status: string; report: string | null; forkable: boolean; steps?: { at: string; title: string; detail?: string }[]; plan?: { n: number; title: string; status: string }[] }
type ReplayPlayerProps = { title: string; steps: unknown[]; report: string | null; fork?: () => void };
const ReplayPlayer = optionalComponent<ReplayPlayerProps>(import.meta.glob('../features/agents/ReplayTab.tsx'), 'ReplayPlayer');

export function ReplayRoute() {
  const { token = '' } = useParams();
  const s = useSession();
  const navigate = useNavigate();
  const loc = useLocation();
  const q = useQuery({ queryKey: ['replay', token], queryFn: async () => (await api.get<{ replay: Replay }>(API.replay(token))).replay, retry: false, staleTime: 60_000 });
  const fork = useMutation({ mutationFn: () => api.post<{ task: { id: string } }>(API.replayFork(token)), onSuccess: (r) => navigate(`/agents?task=${encodeURIComponent(r.task.id)}`) });
  const r = q.data;
  const canFork = !!r?.forkable && s.status === 'authenticated';
  const fallback = r ? (
    <Card title="Steps">
      <ol className="small">{(r.plan ?? []).map((p) => <li key={p.n}>{p.title} <span className="muted">({p.status})</span></li>)}</ol>
      {r.report && <MarkdownStream>{r.report}</MarkdownStream>}
    </Card>
  ) : null;
  return (
    <PublicLayout title={r?.title ?? 'Task replay'}>
      {q.isPending && <Skeleton height={200} />}
      {q.isError && <Banner tone="danger" title="This replay is not available">{errText(q.error)}</Banner>}
      {r && (
        <div className="stack" data-cap="AGENT-17">
          <h1 style={{ font: 'var(--t-title-l)' }}>{r.title}</h1>
          <p className="muted">{r.goal}</p>
          <OptionalSlot C={ReplayPlayer} fallback={fallback} props={{ title: 'Replay', steps: r.steps ?? [], report: r.report, fork: canFork ? () => fork.mutate() : undefined }} />
          {r.forkable && s.status !== 'authenticated' && <p className="small"><Link to="/welcome" state={{ from: loc.pathname }}>Sign in</Link> to fork this task into your own account.</p>}
          {fork.isError && <Banner tone="danger" title="Could not fork">{errText(fork.error)}</Banner>}
        </div>
      )}
    </PublicLayout>
  );
}

/* ---------- /p/:slug ---------- */
type ReportDialogProps = { open: boolean; onClose: () => void; targetType: string; targetId: string };
const ReportDialog = optionalComponent<ReportDialogProps>(import.meta.glob('../features/settings/ReportDialog.tsx'), 'ReportDialog');

export function SiteInfoRoute() {
  const { slug = '' } = useParams();
  const [report, setReport] = useState(false);
  const href = `/p/${encodeURIComponent(slug)}/`;
  return (
    <PublicLayout title={`Site ${slug}`}>
      <section className="stack" data-cap="BUILD-03" aria-label="Published site">
        <div className="row"><Globe size={20} aria-hidden="true" /><h1 style={{ font: 'var(--t-title-l)' }}>{slug}</h1></div>
        <p>This is a site someone published with Crosspoint AI +. It runs on its own isolated address, so it cannot read your Crosspoint account.</p>
        <div className="row">
          <a className="btn primary" href={href} target="_blank" rel="noopener noreferrer"><ExternalLink size={14} aria-hidden="true" /> Open the site</a>
          {ReportDialog && <Button variant="ghost" icon={<Flag size={14} aria-hidden="true" />} onClick={() => setReport(true)}>Report this site</Button>}
        </div>
        <p className="small muted">Sites can be built with AI. Do not enter passwords or payment details unless you trust the person who published it.</p>
      </section>
      {report && <OptionalSlot C={ReportDialog} props={{ open: true, onClose: () => setReport(false), targetType: 'link', targetId: href }} />}
    </PublicLayout>
  );
}

/* ---------- /share-target ---------- */
interface SharedPayload { id?: string; title?: string | null; text?: string | null; url?: string | null; files?: { id: string; name: string | null; mime: string }[] }

/** What the OS share sheet sent: a stored share (`?share=<id>`) or the fields themselves (GET share, or a signed-out POST). */
export function ShareTargetRoute() {
  const [params] = useSearchParams();
  const s = useSession();
  const navigate = useNavigate();
  const loc = useLocation();
  const shareId = params.get('share');
  const fromQuery = useMemo<SharedPayload>(() => ({ title: params.get('title'), text: params.get('text'), url: params.get('url') }), [params]);
  const q = useQuery({
    queryKey: ['share-target', shareId],
    queryFn: async () => (await api.get<{ share: SharedPayload }>(API.shareTargetItem(shareId!))).share,
    enabled: !!shareId && s.status === 'authenticated',
    retry: false,
  });
  const p: SharedPayload = q.data ?? fromQuery;
  const prompt = [p.title, p.text, p.url].filter(Boolean).join('\n');
  const signedIn = s.status === 'authenticated';
  const startChat = () => navigate(shareId ? `/chat/new?share=${encodeURIComponent(shareId)}` : `/chat/new?q=${encodeURIComponent(prompt)}`);
  return (
    <PublicLayout title="Shared to Crosspoint AI +">
      <section className="stack" data-cap="DEV-06" aria-label="Shared content">
        <h1 style={{ font: 'var(--t-title-l)' }}>Shared to Crosspoint AI +</h1>
        {q.isError && <Banner tone="danger" title="This share could not be read">{errText(q.error)}</Banner>}
        {!prompt && !p.files?.length && !q.isPending && <p className="muted">Nothing was shared. Use your device's Share button and pick Crosspoint AI +.</p>}
        {(prompt || p.files?.length) ? (
          <Card>
            {p.title && <b>{p.title}</b>}
            {p.text && <p style={{ whiteSpace: 'pre-wrap' }}>{p.text}</p>}
            {p.url && <p className="small"><a href={p.url} target="_blank" rel="noopener noreferrer">{p.url}</a></p>}
            {p.files?.length ? <ul className="small">{p.files.map((f) => <li key={f.id}>{f.name ?? f.id} <span className="muted">{f.mime}</span></li>)}</ul> : null}
          </Card>
        ) : null}
        {params.get('signin') === '1' && !signedIn && <Banner tone="info" title="Sign in to keep this">Your share was not saved because you were signed out. Sign in, then share it again or start a chat below.</Banner>}
        {signedIn ? (
          <div className="row">
            <Button variant="primary" onClick={startChat} disabled={!prompt && !shareId}>Start a chat with it</Button>
            {shareId && <Button onClick={() => navigate(`/drive?share=${encodeURIComponent(shareId)}`)}>Save to Drive</Button>}
          </div>
        ) : (
          <Link className="btn primary" to="/welcome" state={{ from: loc.pathname + loc.search }}>Sign in to continue</Link>
        )}
      </section>
    </PublicLayout>
  );
}
